import React, { useRef } from "react";
import moment from "moment-timezone";
import { useIntl } from "react-intl";
import { useSolrContext } from "@e-fork/solr";
import useStickyFilterVanish from "@default/hooks/use-sticky-filter-vanish";
import NoResults from "../components/system/no-results";
import Loading from "../components/system/loading";
import Error from "../components/system/error";
import Pagination from "../components/system/pagination";
import EndlessScroll from "../components/system/endless-scroll";
import Filters from "../components/filters";
import appearanceSchemaMapper from "@default/teasers/appearance/appearance-schema-mapper";
import TeaserAppearanceTimetable from "@default/teasers/appearance/teaser-appearance-timetable";
import FilterModal from "../components/system/filter-modal";
import FiltersControlFacet from "../components/filters/filters-control-facet";
import FiltersControlSorting from "../components/filters/filters-control-sorting";
import { getGroupId } from "../components/filters/facet-types/scroll-to-facet";
import FilterModalToggle from "../components/system/filter-modal-toggle";
import ErrorBoundary from "@default/js/error-boundary";
import { useSelector } from "react-redux";
import ViewSwitch from "@default/paragraphs/overview/components/main-controls/view-switch";
import useScreenSize from "@pro/lib/use-screen-size";

const getGroupKey = (item, sorting, tbaLabel) => {
  if (sorting === "date_start:asc") {
    const timestamp = item.fieldDateRawField?.first?.value;
    if (!timestamp) return "tba";
    return moment.unix(timestamp).format("YYYY-MM-DDTHH");
  }
  if (sorting === "venue_location_name:asc") {
    return item.fieldVenueLocation?.label || tbaLabel;
  }
  return null;
};

const getGroupLabel = (key, sorting, tbaLabel) => {
  if (sorting === "date_start:asc") {
    return key === "tba" ? tbaLabel : moment(key, "YYYY-MM-DDTHH").format("HH:mm");
  }
  return key; // venue name or TBA
};

const groupItems = (mappedItems, sorting, tbaLabel) => {
  const map = new Map();

  for (const item of mappedItems) {
    const key = getGroupKey(item, sorting, tbaLabel);
    if (key === null) continue;
    if (!map.has(key)) {
      map.set(key, []);
    }
    map.get(key).push(item);
  }

  return Array.from(map.entries()).map(([key, items]) => ({
    key,
    items:
      sorting === "venue_location_name:asc"
        ? [...items].sort(
            (a, b) =>
              (a.fieldDateRawField?.first?.value ?? 0) -
              (b.fieldDateRawField?.first?.value ?? 0)
          )
        : items,
  }));
};

const OverviewTimetable = ({ content }) => {
  const isMobile = useSelector((state) => state.appStore.isMobile);
  const { items, error, status, config, currentSorting, currentFilters } =
    useSolrContext();
  const persistViewSwitch = useSelector(
    (reduxStore) => reduxStore.appStore.persistViewSwitch
  );
  const filterModal = useRef();
  const { sentinelRef, filterWrapperRef } = useStickyFilterVanish();
  const releaseState = useSelector((state) => state.appStore.releaseState);
  const timetableBreakpoint = useSelector(
    (reduxStore) => reduxStore.appStore.timetableBreakpoint
  );
  const isConferenceOverviewPage = useSelector(
    (reduxStore) => reduxStore.appStore.isConferenceOverviewPage
  );

  const tabletUp = !useScreenSize("md");

  const intl = useIntl();

  const weekdayFacet = config.facets.find(
    (f) => f?.id === "appearance_weekday"
  );
  const timeFacet = config.facets.find((f) => f?.id === "appearance_time");
  const venueFacet = config.facets.find((f) => f?.id === "appearance_venue");

  const isDateSort = currentSorting === "date_start:asc";
  const isVenueSort = currentSorting === "venue_location_name:asc";
  const hasWeekdayFilter = !!currentFilters?.appearance_weekday?.length;
  const tbaLabel = intl.formatMessage({ id: "tba" });

  const mappedItems = items?.map(appearanceSchemaMapper) ?? [];
  const groups =
    isDateSort || isVenueSort
      ? groupItems(mappedItems, currentSorting, tbaLabel).map((g) => ({
          ...g,
          label: getGroupLabel(g.key, currentSorting, tbaLabel),
        }))
      : [{ key: null, label: null, items: mappedItems }];

  const filterModalTags = ["modal"];
  if (content.fieldViewType === "conference_event") {
    filterModalTags.push("conference");
  } else {
    filterModalTags.push("music");
  }

  return (
    <section
      className={`paragraph paragraph-overview overview-solr type-${content.fieldContentType}`}
    >
      <div className="container">
        <div ref={sentinelRef} />
        <div
          ref={filterWrapperRef}
          className="row sticky-wrapper filter-wrapper"
        >
          <div className="col-12">
            <div
              className={`d-flex gap-xs ${
                isMobile && !releaseState.showDay && !releaseState.showDate
                  ? "justify-content-start"
                  : "justify-content-center"
              }`}
            >
              {isDateSort &&
                hasWeekdayFilter &&
                timeFacet &&
                releaseState.showDate &&
                !isMobile && (
                  <div className="flex-grow-1">
                    <FiltersControlFacet
                      id={timeFacet.id}
                      label={timeFacet.label}
                      type={timeFacet.type}
                      autoJumpToCurrentTime
                    />
                  </div>
                )}
              {isVenueSort && venueFacet && (
                <div className="flex-grow-1">
                  <FiltersControlFacet
                    id={venueFacet.id}
                    label={venueFacet.label}
                    type={venueFacet.type}
                  />
                </div>
              )}

              <>
                {tabletUp ? (
                  <>
                    <div
                      className={`center-wrapper flex-grow-1 ${
                        releaseState.showDate
                          ? "flex-lg-grow-0"
                          : "flex-lg-grow-1 justify-content-lg-center"
                      } d-flex gap-xs`}
                    >
                      {!isMobile &&
                        (persistViewSwitch || isConferenceOverviewPage) &&
                        timetableBreakpoint && <ViewSwitch />}
                      {releaseState.showDate && (
                        <div className="filters-controlsection">
                          <div className="filters-controlsection-controls">
                            <FiltersControlSorting />
                          </div>
                        </div>
                      )}

                      {releaseState.showDay && (
                        <div className="filters-controlsection">
                          <div className="filters-controlsection-controls">
                            <FiltersControlFacet
                              id={weekdayFacet.id}
                              label={weekdayFacet.label}
                              key={weekdayFacet.id}
                              multiple={weekdayFacet.multiple}
                              type={weekdayFacet.type}
                              isFrontendId={weekdayFacet.isFrontendId}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                    <div
                      className={`right-wrapper d-flex ${
                        releaseState.showDate
                          ? "flex-lg-grow-1"
                          : "flex-lg-grow-0"
                      } justify-content-end`}
                    >
                      <ErrorBoundary>
                        <FilterModalToggle
                          onClick={() => filterModal.current.open()}
                          label="Filter"
                          excludeFromCount={["appearance_weekday"]}
                        />
                      </ErrorBoundary>
                    </div>
                  </>
                ) : (
                  <div className="mobile-filter-wrapper">
                    <div className="filters-controlsection">
                      <div className="filters-controlsection-controls d-flex gap-xs flex-grow-1">
                        {releaseState.showDay && (
                          <FiltersControlFacet
                            id={weekdayFacet.id}
                            label={weekdayFacet.label}
                            key={weekdayFacet.id}
                            multiple={weekdayFacet.multiple}
                            type={weekdayFacet.type}
                            isFrontendId={weekdayFacet.isFrontendId}
                          />
                        )}
                        <div className="d-flex gap-xs flex-grow-1 justify-content-between">
                          {releaseState.showDay && <FiltersControlSorting />}

                          <FilterModalToggle
                            onClick={() => filterModal.current.open()}
                            label="Filter"
                            excludeFromCount={["appearance_weekday"]}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </>
            </div>
            <ErrorBoundary>
              <FilterModal ref={filterModal}>
                <Filters tags={filterModalTags} />
              </FilterModal>
            </ErrorBoundary>
          </div>
        </div>
        <div className="timetable-main">
          {!!mappedItems.length &&
            !error &&
            status !== "LOADING" &&
            groups.map(({ key, label, items: groupedItems }) => (
              <div
                className="group-wrapper"
                key={key}
                id={key ? getGroupId(key) : undefined}
              >
                {isDateSort && (
                  <div className="group-by-label date d-none">
                    <div className="container">
                      <div className="row">
                        <div className="col-12">
                          <div className="label">{label}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {isVenueSort && (
                  <div className="group-by-label venue headline-xl blurred-background viewport-width">
                    <div className="container">
                      <div className="row">
                        <div className="col-12">
                          <div className="label">{label}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {groupedItems.map((item) => (
                  <TeaserAppearanceTimetable item={item} key={item.id} />
                ))}
              </div>
            ))}
          <NoResults />
          <Loading />
          <Error />
        </div>

        {config.pageMode === "pager" && <Pagination />}
        {config.pageMode === "endless" && <EndlessScroll />}
      </div>
    </section>
  );
};

OverviewTimetable.propTypes = {};

export default OverviewTimetable;
